/**
 * UserManagement.tsx - 用户管理页面
 * 管理员创建、编辑、删除BD用户账户
 */

import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Typography,
  Tooltip,
  Switch,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  UserOutlined,
  MailOutlined,
  CopyOutlined,
  LogoutOutlined,
} from '@ant-design/icons'
import { authApi } from '../api/client'
import { useAuthStore, type User } from '../store'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Option } = Select

const USER_STATUS_MAP: Record<string, { color: string; label: string }> = {
  active: { color: 'green', label: '在职' },
  inactive: { color: 'default', label: '已离职' },
  suspended: { color: 'orange', label: '已暂停' },
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [departModalVisible, setDepartModalVisible] = useState(false)
  const [departingUser, setDepartingUser] = useState<User | null>(null)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState<string | null>(null)
  const [form] = Form.useForm()
  const [departForm] = Form.useForm()
  const { user: currentUser } = useAuthStore()

  // 加载用户列表
  const loadUsers = async () => {
    setLoading(true)
    try {
      const data = await authApi.getUsers()
      setUsers(data)
    } catch (error) {
      message.error('加载用户列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  // 打开创建/编辑模态框
  const openModal = (user?: User) => {
    if (user) {
      setEditingUser(user)
      form.setFieldsValue({
        email: user.email,
        name: user.name,
        role: user.role,
        is_active: user.is_active,
        user_status: (user as any).user_status || 'active',
      })
    } else {
      setEditingUser(null)
      form.resetFields()
      form.setFieldsValue({ role: 'bd', is_active: true, user_status: 'active' })
    }
    setModalVisible(true)
  }

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      if (editingUser) {
        await authApi.updateUser(editingUser.id, {
          name: values.name,
          is_active: values.is_active,
          user_status: values.user_status,
        })
        message.success('用户更新成功')
      } else {
        await authApi.createUser({
          email: values.email,
          password: values.password,
          name: values.name,
          role: values.role,
        })
        message.success('用户创建成功')
      }

      setModalVisible(false)
      loadUsers()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || '操作失败'
      message.error(errorMsg)
    }
  }

  // 删除用户
  const handleDelete = async (userId: string) => {
    try {
      await authApi.deleteUser(userId)
      message.success('用户删除成功')
      loadUsers()
    } catch (error) {
      message.error('删除失败')
    }
  }

  // 重置密码
  const handleResetPassword = async (userId: string) => {
    try {
      const result = await authApi.resetUserPassword(userId)
      setNewPassword(result.new_password)
      message.success('密码重置成功')
    } catch (error) {
      message.error('密码重置失败')
    }
  }

  // 复制密码
  const copyPassword = () => {
    if (newPassword) {
      navigator.clipboard.writeText(newPassword)
      message.success('密码已复制到剪贴板')
    }
  }

  // 打开离职处理模态框
  const openDepartModal = (user: User) => {
    setDepartingUser(user)
    departForm.resetFields()
    setDepartModalVisible(true)
  }

  // 提交离职处理
  const handleDepart = async () => {
    try {
      const values = await departForm.validateFields()
      if (!departingUser) return

      const result = await authApi.departUser(departingUser.id, {
        handover_to_user_id: values.handover_to_user_id || undefined,
        departure_note: values.departure_note || undefined,
      })

      const msg = values.handover_to_user_id
        ? `离职处理完成：${result.projects_transferred} 个项目已转移`
        : `离职处理完成：${result.projects_conflict_flagged} 个项目已标记冲突`
      message.success(msg)

      setDepartModalVisible(false)
      loadUsers()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || '离职处理失败'
      message.error(errorMsg)
    }
  }

  const activeBdUsers = users.filter(
    (u) => u.role === 'bd' && u.is_active && u.id !== departingUser?.id,
  )

  const columns = [
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: User) => (
        <Space>
          <UserOutlined />
          <Text strong>{name}</Text>
          {record.id === currentUser?.id && <Tag color="blue">当前用户</Tag>}
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ),
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role === 'admin' ? '管理员' : 'BD'}
        </Tag>
      ),
    },
    {
      title: '状态',
      key: 'status',
      render: (_: any, record: User) => {
        const status = (record as any).user_status || (record.is_active ? 'active' : 'inactive')
        const { color, label } = USER_STATUS_MAP[status] || { color: 'default', label: status }
        return <Tag color={color}>{label}</Tag>
      },
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date: string) =>
        date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openModal(record)}
            />
          </Tooltip>
          <Tooltip title="重置密码">
            <Popconfirm
              title="确定要重置该用户的密码吗？"
              description="将生成新的随机密码"
              onConfirm={() => handleResetPassword(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="text" icon={<KeyOutlined />} />
            </Popconfirm>
          </Tooltip>
          {record.id !== currentUser?.id && record.role === 'bd' && (
            <Tooltip title="离职处理">
              <Button
                type="text"
                danger
                icon={<LogoutOutlined />}
                onClick={() => openDepartModal(record)}
                disabled={(record as any).user_status === 'inactive'}
              />
            </Tooltip>
          )}
          {record.id !== currentUser?.id && (
            <Tooltip title="删除">
              <Popconfirm
                title="确定要删除该用户吗？"
                description="删除后该用户的所有数据将被清除"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="user-management-page">
      <Card
        title={
          <Space>
            <UserOutlined />
            <span>用户管理</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            添加用户
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
          }}
        />
      </Card>

      {/* 创建/编辑用户模态框 */}
      <Modal
        title={editingUser ? '编辑用户' : '创建用户'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingUser ? '保存' : '创建'}
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="user@taskon.xyz"
              disabled={!!editingUser}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="输入用户名" />
          </Form.Item>

          {!editingUser && (
            <>
              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少6个字符' },
                ]}
              >
                <Input.Password placeholder="输入密码" />
              </Form.Item>

              <Form.Item
                name="role"
                label="角色"
                rules={[{ required: true, message: '请选择角色' }]}
              >
                <Select>
                  <Option value="bd">BD (普通用户)</Option>
                  <Option value="admin">管理员</Option>
                </Select>
              </Form.Item>
            </>
          )}

          {editingUser && (
            <>
              <Form.Item name="user_status" label="用户状态">
                <Select>
                  <Option value="active">在职（正常）</Option>
                  <Option value="suspended">已暂停（临时停用）</Option>
                  <Option value="inactive">已离职</Option>
                </Select>
              </Form.Item>
              <Form.Item name="is_active" label="账户启用" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* 离职处理模态框 */}
      <Modal
        title={`BD 离职处理 - ${departingUser?.name}`}
        open={departModalVisible}
        onOk={handleDepart}
        onCancel={() => setDepartModalVisible(false)}
        okText="确认离职"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        <Form form={departForm} layout="vertical">
          <Form.Item
            name="handover_to_user_id"
            label="项目交接给"
            extra="若不选择交接人，该 BD 名下有 twitter_handle 的项目将被标记为冲突"
          >
            <Select allowClear placeholder="选择接收人（可不填）">
              {activeBdUsers.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="departure_note" label="离职备注">
            <Input.TextArea rows={3} placeholder="可选，填写离职原因或备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 新密码显示模态框 */}
      <Modal
        title="密码重置成功"
        open={!!newPassword}
        onOk={() => setNewPassword(null)}
        onCancel={() => setNewPassword(null)}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={copyPassword}>
            复制密码
          </Button>,
          <Button key="ok" type="primary" onClick={() => setNewPassword(null)}>
            确定
          </Button>,
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Text>新密码：</Text>
          <Text code strong style={{ fontSize: 18 }}>
            {newPassword}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">请妥善保管此密码，关闭后将无法再次查看</Text>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default UserManagement
